import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../store';
import { getCookie, setCookie, convertMinutesToMs } from '../utils/helpers';
import { detectGpc, getGpcBrowserMetadata } from '../utils/gpc';
import { trackGpcDetected } from '../utils/vysion/privacy';
import { updateCookieConsentValue } from '../actions/settingActions';

interface UseCookieConsentCheckResult {
  shouldShowModal: boolean;
  isQualified: boolean | undefined;
  isLoading: boolean;
  isGpcActive: boolean;
}

// Module-level ref to ensure GPC tracking only fires once across all hook instances
let gpcTrackedRef = false;

/**
 * Hook to check if cookie consent modal should be shown for CPPA-qualified users
 * Implements REQ-1a logic for California Privacy Protection Agency compliance
 */
const useCookieConsentCheck = (): UseCookieConsentCheckResult => {
  const dispatch = useDispatch();
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);
  const [isQualified, setIsQualified] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [gpcActive, setGpcActive] = useState<boolean>(false);

  // Get settings from Redux store
  const settings = useSelector((state: RootState) => state.settings);
  const profile = useSelector((state: RootState) => state.profile?.profile?.data);
  const location = useLocation();

  useEffect(() => {
    const checkCookieConsent = (): void => {
      setIsLoading(true);

      // Check if cookie_consent feature is enabled and configured
      if (!settings?.cookie_consent || !settings.cookie_consent.enabled) {
        setShouldShowModal(false);
        setIsQualified(undefined);
        setIsLoading(false);
        setGpcActive(false);
        return;
      }

      // Task 3.1: Check if user location qualifies for CPPA
      const userRegion = profile?.region;
      const userCountry = profile?.country;

      if (!userRegion || !userCountry) {
        // Location not yet determined
        setShouldShowModal(false);
        setIsQualified(undefined);
        setIsLoading(false);
        setGpcActive(false);
        return;
      }

      // Check if user's country and region are in the CPPA locations
      const locations = settings.cookie_consent.locations;
      const qualifyingRegions = locations[userCountry] || [];

      const isUserQualified = qualifyingRegions.some(
        (region: string) => region.toLowerCase() === userRegion.toLowerCase()
      );

      // Save qualification status to localStorage for use in index-template.html
      localStorage.setItem('isUserCppaQualified', JSON.stringify(isUserQualified));

      setIsQualified(isUserQualified);

      if (!isUserQualified) {
        setShouldShowModal(false);
        setIsLoading(false);
        setGpcActive(false);
        return;
      }

      // REQ-1b: Check for GPC browser setting
      // GPC check must occur after location determination (California check)
      const gpcDetection = detectGpc();
      const isGpcSignalActive = gpcDetection.isActive;

      setGpcActive(isGpcSignalActive);

      // If GPC is active, automatically apply opt-out and prevent modal display
      if (isGpcSignalActive) {
        // Get expiry time from settings or default to 1 year (525600 minutes)
        const expiryMinutes = settings.cookie_consent.actionExpiryMins || 525600;
        const expiryMs = convertMinutesToMs(expiryMinutes);

        // Override any existing cookie preference with "rejected" when GPC is active
        // Use cookie as source of truth to prevent infinite loop
        const currentCookieValue = getCookie('cookie_consent');

        // Only update cookie and dispatch if cookie is not already 'rejected'
        // This prevents infinite loop: cookie changes don't trigger useEffect,
        // and we only dispatch when we actually change the cookie
        if (currentCookieValue !== 'rejected') {
          setCookie('cookie_consent', 'rejected', false, expiryMs);
          dispatch(updateCookieConsentValue('rejected'));
        }

        // Track GPC detection event (only once per session, only on /join page)
        // IMPORTANT: This event is for compliance monitoring only
        // and MUST NOT be used for profiling or behavioral advertising
        // Per spec: Event should only fire on /join page
        // Use module-level ref to prevent multiple instances from firing
        if (!gpcTrackedRef && location.pathname === '/join') {
          const browserMetadata = getGpcBrowserMetadata();

          trackGpcDetected({
            ...browserMetadata,
            userAgent: browserMetadata.userAgent as string | undefined,
            platform: browserMetadata.platform as string | undefined,
            language: browserMetadata.language as string | undefined,
            gpcSupported: browserMetadata.gpcSupported as boolean | undefined,
            gpcActive: browserMetadata.gpcActive as boolean | undefined,
            preferenceState: 'deny',
          });

          gpcTrackedRef = true;
        }

        // Prevent modal from showing when GPC is active
        setShouldShowModal(false);
        setIsLoading(false);
        return;
      }

      // Task 3.2: Read cookie_consent cookie
      const cookieValue = getCookie('cookie_consent');

      // Task 3.3: Implement three REQ-1a scenarios
      if (!cookieValue || cookieValue === 'closed') {
        // Scenario 1: No cookie or closed - show modal
        setShouldShowModal(true);
      } else if (cookieValue === 'rejected') {
        // Scenario 2: Rejected - do not show modal, deny behavior applied elsewhere
        setShouldShowModal(false);
      } else if (cookieValue === 'accepted') {
        // Scenario 3: Accepted - do not show modal, proceed normally
        setShouldShowModal(false);
      } else {
        // Unknown cookie value - treat as no cookie
        setShouldShowModal(true);
      }

      setIsLoading(false);
    };

    checkCookieConsent();
    // Note: cookieConsentValue is intentionally excluded from dependencies to prevent
    // infinite loop when GPC is active. The cookie value is used as source of truth instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.cookie_consent?.enabled, profile?.data?.region, profile?.data?.country, location.pathname]);

  return {
    shouldShowModal,
    isQualified,
    isLoading,
    isGpcActive: gpcActive,
  };
};

export default useCookieConsentCheck;
